/**
 * Modal "Show your work" — credibilidad del Score Alpha.
 *
 * Lazy-loaded: solo monta cuando el usuario hace click en
 * "¿Cómo se calcula?" en la Ficha Municipal. Hace fetch a
 * `GET /municipalities/{dane}/score-details`.
 *
 * Renderiza 6 secciones (ver PRD sección 4 + system design):
 *  1. Fórmula textual con valores reales
 *  2. Lista expandible de peer group (DANE + nombre + departamento)
 *  3. Ventana temporal (acumulada — toda la historia, ADR-017)
 *  4. Filtros de status aplicados
 *  5. Lista expandible de proyectos SGR del muni
 *  6. Disclaimer honesto
 *
 * Cierre: ESC, click en overlay, o botón ×.
 *
 * Ver:
 *  - PRD: docs/product/ficha-municipal-prd.md
 *  - System design: docs/engineering/architecture/ficha-municipal.md
 *  - ADR-017: docs/engineering/adrs/ADR-017-score-alpha-design.md
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { NombreMunicipio } from "@/components/NombreMunicipio";
import { ApiError, fetchMunicipalityScoreDetails, type ScoreDetailsDTO } from "@/lib/api";

/**
 * `municipioAjeno` (issue #746): nombre del municipio de la ficha cuando no
 * es el del usuario. Con valor, el modal lo nombra en vez de hablar de "tu"
 * indicador. La fórmula la arma el backend y ya es neutra ("Este municipio").
 */
export function ShowYourWorkModal({
  dane,
  onClose,
  municipioAjeno = null,
}: {
  dane: string;
  onClose: () => void;
  municipioAjeno?: string | null;
}) {
  const details = useQuery({
    queryKey: ["muni-score-details", dane],
    queryFn: () => fetchMunicipalityScoreDetails(dane),
    enabled: Boolean(dane),
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
        return false;
      }
      return count < 2;
    },
  });

  // Cierre con ESC + bloqueo de scroll del body mientras está abierto.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      data-testid="show-work-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="show-work-title"
      className="fixed inset-0 z-100 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 id="show-work-title" className="text-lg font-bold text-slate-900">
              ¿Cómo se calcula{" "}
              {municipioAjeno ? (
                <>
                  el indicador de <NombreMunicipio nombre={municipioAjeno} />
                </>
              ) : (
                "tu indicador"
              )}
              ?
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Transparencia total. Audita la fórmula y la data.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="show-work-close"
            aria-label="Cerrar"
            className="-mr-2 rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[calc(100vh-12rem)] overflow-y-auto px-6 py-5">
          {details.isLoading ? (
            <ModalSkeleton />
          ) : details.isError ? (
            <ModalError error={details.error} />
          ) : details.data ? (
            <ModalContent data={details.data} municipioAjeno={municipioAjeno} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ModalContent({
  data,
  municipioAjeno,
}: {
  data: ScoreDetailsDTO;
  municipioAjeno: string | null;
}) {
  return (
    <div className="space-y-6">
      {/* 1. Fórmula */}
      <section data-testid="show-work-formula">
        <h3 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Fórmula</h3>
        <p className="mt-2 rounded-md bg-slate-50 px-3 py-2 font-mono text-xs leading-relaxed text-slate-800">
          {data.formula_text}
        </p>
      </section>

      {/* 2. Periodo considerado */}
      <section data-testid="show-work-window">
        <h3 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
          Periodo considerado
        </h3>
        <p className="mt-2 text-sm text-slate-700">
          Toda la historia disponible de proyectos SGR del municipio (sin filtro temporal). La
          fuente pública no expone fecha de aprobación por proyecto, por eso no se segmenta por
          vigencia.
        </p>
      </section>

      {/* 2.5 ¿Por qué solo SGR? — el "alcance" del indicador.
          Crítico para que el usuario no piense que Kaptar = SGR.
          El Radar es multi-fuente; este indicador comparativo es
          solo SGR porque es la única fuente con histórico abierto
          + comparable + cobertura universal (verificado mayo 2026). */}
      <section
        data-testid="show-work-scope"
        className="rounded-md border border-brand-100 bg-brand-50/40 p-3"
      >
        <h3 className="text-xs font-semibold tracking-wider text-brand-700 uppercase">
          ¿Por qué solo SGR?
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          El SGR es la fuente con histórico abierto comparable entre los 1.100+ municipios
          colombianos. Otras fuentes que Kaptar agrega (cooperación internacional, MinTIC,
          MinEnergía, fondos sectoriales) no tienen datasets históricos públicos por municipio, así
          que hoy no se pueden comparar entre pares.
        </p>
        <p className="mt-2 text-sm leading-relaxed font-medium text-slate-700">
          Las convocatorias activas de <strong>todas las fuentes</strong> las ves en el Radar. Este
          indicador mide solo{" "}
          {municipioAjeno ? (
            <>
              la trayectoria histórica de captación SGR de{" "}
              <NombreMunicipio nombre={municipioAjeno} />
            </>
          ) : (
            "tu trayectoria histórica de captación SGR"
          )}{" "}
          — la fuente más grande del bolsillo municipal.
        </p>
      </section>

      {/* 3. Estados incluidos */}
      <section data-testid="show-work-status-filter">
        <h3 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
          Estados incluidos
        </h3>
        <div className="mt-2 flex flex-wrap gap-2">
          {data.status_filter_included.map((s) => (
            <span
              key={s}
              className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
            >
              {s}
            </span>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Excluye RECHAZADO y ANULADO porque no representan captación efectiva.
        </p>
      </section>

      {/* 4. Municipios similares */}
      <section data-testid="show-work-peer-group">
        <h3 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
          Municipios similares ({data.peer_group.length}{" "}
          {data.peer_group.length === 1 ? "municipio" : "municipios"})
        </h3>
        {data.peer_group.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            No hay grupo de comparación local — el indicador puede estar usando el promedio nacional
            como referencia.
          </p>
        ) : (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-semibold text-brand-700 select-none hover:underline">
              Ver lista completa →
            </summary>
            <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-md border border-slate-100 p-2">
              {data.peer_group.map((p) => (
                <li key={p.dane_code} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-slate-800">{p.name}</span>
                  <span className="text-slate-500">{p.department_name}</span>
                  <span className="font-mono text-slate-400">{p.dane_code}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* 5. Proyectos del muni */}
      <section data-testid="show-work-projects">
        <h3 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
          Proyectos SGR del muni ({data.muni_projects.length})
        </h3>
        {data.muni_projects.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu municipio"} no tiene
            proyectos SGR registrados en el dataset.
          </p>
        ) : (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-semibold text-brand-700 select-none hover:underline">
              Ver los {data.muni_projects.length} proyectos que entran en el cálculo →
            </summary>
            <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-slate-100">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-left">
                  <tr>
                    <th className="px-2 py-1 font-semibold text-slate-600">BPIN</th>
                    <th className="px-2 py-1 font-semibold text-slate-600">Nombre</th>
                    <th className="px-2 py-1 font-semibold text-slate-600">Sector</th>
                    <th className="px-2 py-1 text-right font-semibold text-slate-600">Monto</th>
                    <th className="px-2 py-1 font-semibold text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.muni_projects.map((p) => (
                    <tr key={p.bpin} className="border-t border-slate-100 align-top">
                      <td className="px-2 py-1 font-mono text-slate-500">{p.bpin}</td>
                      <td className="px-2 py-1 text-slate-800">{p.name}</td>
                      <td className="px-2 py-1 text-slate-600">{p.sector.toLowerCase()}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-700">
                        {formatCopShort(p.amount_approved_cop)}
                      </td>
                      <td className="px-2 py-1 text-slate-600">{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </section>

      {/* 6. Disclaimer */}
      <section
        data-testid="show-work-disclaimer"
        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
      >
        <p className="text-xs leading-relaxed text-amber-900">{data.disclaimer}</p>
      </section>
    </div>
  );
}

function ModalSkeleton() {
  return (
    <div data-testid="show-work-skeleton" className="animate-pulse space-y-5">
      <div className="h-12 rounded-md bg-slate-100" />
      <div className="h-16 rounded-md bg-slate-100" />
      <div className="h-10 rounded-md bg-slate-100" />
      <div className="h-24 rounded-md bg-slate-100" />
      <div className="h-32 rounded-md bg-slate-100" />
    </div>
  );
}

function ModalError({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError && error.status === 404
      ? "Aún no tenemos los detalles del score para este municipio. Vuelve mañana."
      : error instanceof Error
        ? error.message
        : "Error cargando los detalles del cálculo.";
  return (
    <div
      data-testid="show-work-error"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800"
    >
      {message}
    </div>
  );
}

function formatCopShort(amount: string | null): string {
  if (!amount) return "—";
  // amount viene como string ('123456789.00') para preservar precisión Decimal.
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}B`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}MM`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString("es-CO")}`;
}
